// Feature Codes
export const FEATURE_CODES = {
  TRANSACTION: 'TRANSACTION',
  INVOICE: 'INVOICE',
  MEMBER: 'MEMBER',
  MERCHANT: 'MERCHANT',
  PROGRAM: 'PROGRAM',
  USER: 'USER',
  OFFERS_FOR_PROGRAM: 'OFFERS_FOR_PROGRAM',
  REDEMPTION_MERCHANT: 'REDEMPTION_MERCHANT',
  SHIPPING_PROFILE: 'SHIPPING_PROFILE',
  BRAND: 'BRAND',
  ORDER: 'ORDER',
  PRODUCT: 'PRODUCT',
  PLATFORM_CATEGORY: 'PLATFORM_CATEGORY',
  PROGRAM_CATEGORY: 'PROGRAM_CATEGORY',
  TEMPLATE: 'TEMPLATE',
  TRANSFER_PARTNER: 'TRANSFER_PARTNER',
  MERCHANT_OFFER: 'MERCHANT_OFFER',
  MEMBER_ID_VALIDATION: 'MEMBER_ID_VALIDATION',
} as const;

// Permission Types
export const PERMISSION_TYPES = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  VIEW: 'VIEW',
  DELETE: 'DELETE',
} as const;

// Permission Codes
export const PERMISSION_CODES = {
  // Transaction permissions
  CREATE_TRANSACTION: 'CREATE_TRANSACTION',
  UPDATE_TRANSACTION: 'UPDATE_TRANSACTION',
  VIEW_TRANSACTION: 'VIEW_TRANSACTION',
  DELETE_TRANSACTION: 'DELETE_TRANSACTION',

  // Invoice permissions
  CREATE_INVOICE: 'CREATE_INVOICE',
  UPDATE_INVOICE: 'UPDATE_INVOICE',
  VIEW_INVOICE: 'VIEW_INVOICE',
  DELETE_INVOICE: 'DELETE_INVOICE',

  // Member permissions
  CREATE_MEMBER: 'CREATE_MEMBER',
  UPDATE_MEMBER: 'UPDATE_MEMBER',
  VIEW_MEMBER: 'VIEW_MEMBER',
  DELETE_MEMBER: 'DELETE_MEMBER',

  // Merchant permissions
  CREATE_MERCHANT: 'CREATE_MERCHANT',
  UPDATE_MERCHANT: 'UPDATE_MERCHANT',
  VIEW_MERCHANT: 'VIEW_MERCHANT',
  DELETE_MERCHANT: 'DELETE_MERCHANT',

  // Program permissions
  CREATE_PROGRAM: 'CREATE_PROGRAM',
  UPDATE_PROGRAM: 'UPDATE_PROGRAM',
  VIEW_PROGRAM: 'VIEW_PROGRAM',
  DELETE_PROGRAM: 'DELETE_PROGRAM',

  // User permissions
  CREATE_USER: 'CREATE_USER',
  UPDATE_USER: 'UPDATE_USER',
  VIEW_USER: 'VIEW_USER',
  DELETE_USER: 'DELETE_USER',

  // Offer Point (offers app) for program permissions
  VIEW_OFFERS_FOR_PROGRAM: 'VIEW_OFFERS_FOR_PROGRAM',

  // Redemption merchant permissions
  CREATE_REDEMPTION_MERCHANT: 'CREATE_REDEMPTION_MERCHANT',
  UPDATE_REDEMPTION_MERCHANT: 'UPDATE_REDEMPTION_MERCHANT',
  VIEW_REDEMPTION_MERCHANT: 'VIEW_REDEMPTION_MERCHANT',
  DELETE_REDEMPTION_MERCHANT: 'DELETE_REDEMPTION_MERCHANT',

  // Shipping profile permissions
  CREATE_SHIPPING_PROFILE: 'CREATE_SHIPPING_PROFILE',
  UPDATE_SHIPPING_PROFILE: 'UPDATE_SHIPPING_PROFILE',
  VIEW_SHIPPING_PROFILE: 'VIEW_SHIPPING_PROFILE',
  DELETE_SHIPPING_PROFILE: 'DELETE_SHIPPING_PROFILE',

  // Brand permissions
  CREATE_BRAND: 'CREATE_BRAND',
  UPDATE_BRAND: 'UPDATE_BRAND',
  VIEW_BRAND: 'VIEW_BRAND',
  DELETE_BRAND: 'DELETE_BRAND',

  // Order permissions
  CREATE_ORDER: 'CREATE_ORDER',
  UPDATE_ORDER: 'UPDATE_ORDER',
  VIEW_ORDER: 'VIEW_ORDER',
  DELETE_ORDER: 'DELETE_ORDER',

  // Product permissions
  CREATE_PRODUCT: 'CREATE_PRODUCT',
  UPDATE_PRODUCT: 'UPDATE_PRODUCT',
  VIEW_PRODUCT: 'VIEW_PRODUCT',
  DELETE_PRODUCT: 'DELETE_PRODUCT',

  // Platform category permissions
  CREATE_PLATFORM_CATEGORY: 'CREATE_PLATFORM_CATEGORY',
  UPDATE_PLATFORM_CATEGORY: 'UPDATE_PLATFORM_CATEGORY',
  VIEW_PLATFORM_CATEGORY: 'VIEW_PLATFORM_CATEGORY',
  DELETE_PLATFORM_CATEGORY: 'DELETE_PLATFORM_CATEGORY',

  // Program category permissions
  CREATE_PROGRAM_CATEGORY: 'CREATE_PROGRAM_CATEGORY',
  UPDATE_PROGRAM_CATEGORY: 'UPDATE_PROGRAM_CATEGORY',
  VIEW_PROGRAM_CATEGORY: 'VIEW_PROGRAM_CATEGORY',
  DELETE_PROGRAM_CATEGORY: 'DELETE_PROGRAM_CATEGORY',

  // Template permissions
  CREATE_TEMPLATE: 'CREATE_TEMPLATE',
  UPDATE_TEMPLATE: 'UPDATE_TEMPLATE',
  VIEW_TEMPLATE: 'VIEW_TEMPLATE',
  DELETE_TEMPLATE: 'DELETE_TEMPLATE',

  // Transfer partner permissions
  CREATE_TRANSFER_PARTNER: 'CREATE_TRANSFER_PARTNER',
  UPDATE_TRANSFER_PARTNER: 'UPDATE_TRANSFER_PARTNER',
  VIEW_TRANSFER_PARTNER: 'VIEW_TRANSFER_PARTNER',
  DELETE_TRANSFER_PARTNER: 'DELETE_TRANSFER_PARTNER',

  // Merchant offer permissions
  CREATE_MERCHANT_OFFER: 'CREATE_MERCHANT_OFFER',
  UPDATE_MERCHANT_OFFER: 'UPDATE_MERCHANT_OFFER',
  VIEW_MERCHANT_OFFER: 'VIEW_MERCHANT_OFFER',
  DELETE_MERCHANT_OFFER: 'DELETE_MERCHANT_OFFER',

  // Member ID validation permissions
  VIEW_MEMBER_ID_VALIDATION: 'VIEW_MEMBER_ID_VALIDATION',
} as const;

// Permission groups for common operations
export const PERMISSION_GROUPS = {
  // Full CRUD permissions for each feature
  TRANSACTION_FULL: [
    PERMISSION_CODES.CREATE_TRANSACTION,
    PERMISSION_CODES.VIEW_TRANSACTION,
    PERMISSION_CODES.UPDATE_TRANSACTION,
    PERMISSION_CODES.DELETE_TRANSACTION,
  ],
  INVOICE_FULL: [
    PERMISSION_CODES.CREATE_INVOICE,
    PERMISSION_CODES.VIEW_INVOICE,
    PERMISSION_CODES.UPDATE_INVOICE,
    PERMISSION_CODES.DELETE_INVOICE,
  ],
  MEMBER_FULL: [
    PERMISSION_CODES.CREATE_MEMBER,
    PERMISSION_CODES.VIEW_MEMBER,
    PERMISSION_CODES.UPDATE_MEMBER,
    PERMISSION_CODES.DELETE_MEMBER,
  ],
  MERCHANT_FULL: [
    PERMISSION_CODES.CREATE_MERCHANT,
    PERMISSION_CODES.VIEW_MERCHANT,
    PERMISSION_CODES.UPDATE_MERCHANT,
    PERMISSION_CODES.DELETE_MERCHANT,
  ],
  PROGRAM_FULL: [
    PERMISSION_CODES.CREATE_PROGRAM,
    PERMISSION_CODES.VIEW_PROGRAM,
    PERMISSION_CODES.UPDATE_PROGRAM,
    PERMISSION_CODES.DELETE_PROGRAM,
  ],
  USER_FULL: [
    PERMISSION_CODES.CREATE_USER,
    PERMISSION_CODES.VIEW_USER,
    PERMISSION_CODES.UPDATE_USER,
    PERMISSION_CODES.DELETE_USER,
  ],

  // Read-only permissions
  TRANSACTION_READ: [PERMISSION_CODES.VIEW_TRANSACTION],
  INVOICE_READ: [PERMISSION_CODES.VIEW_INVOICE],
  MEMBER_READ: [PERMISSION_CODES.VIEW_MEMBER],
  MERCHANT_READ: [PERMISSION_CODES.VIEW_MERCHANT],
  PROGRAM_READ: [PERMISSION_CODES.VIEW_PROGRAM],
  USER_READ: [PERMISSION_CODES.VIEW_USER],

  // Write permissions (Create + Update)
  TRANSACTION_WRITE: [
    PERMISSION_CODES.CREATE_TRANSACTION,
    PERMISSION_CODES.UPDATE_TRANSACTION,
  ],
  INVOICE_WRITE: [
    PERMISSION_CODES.CREATE_INVOICE,
    PERMISSION_CODES.UPDATE_INVOICE,
  ],
  MEMBER_WRITE: [
    PERMISSION_CODES.CREATE_MEMBER,
    PERMISSION_CODES.UPDATE_MEMBER,
  ],
  MERCHANT_WRITE: [
    PERMISSION_CODES.CREATE_MERCHANT,
    PERMISSION_CODES.UPDATE_MERCHANT,
  ],
  PROGRAM_WRITE: [
    PERMISSION_CODES.CREATE_PROGRAM,
    PERMISSION_CODES.UPDATE_PROGRAM,
  ],
  USER_WRITE: [
    PERMISSION_CODES.CREATE_USER,
    PERMISSION_CODES.UPDATE_USER,
  ],
} as const; 